import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../lib/auth';
import {
  calculatePeriodComparison,
  getDateRangeForPeriod,
  getPreviousPeriodRange,
  formatTrendData,
  type PeriodComparison,
  type TrendData,
} from '../utils/overviewCalculations';
import { type TimeRange } from '../components/reports/TimeRangeSelector';
import { getWeekBounds } from './useSalesHistory';

async function fetchAllProductsForAdmin(adminId: string): Promise<Array<{
  id: string;
  name: string;
  quantity: number | null;
  purchase_price: number | null;
  cost_price: number | null;
  sell_price: number | null;
  unit_price: number | null;
  category: string | null;
  min_stock_level: number | null;
}>> {
  const { count, error: countError } = await supabase
    .from('products')
    .select('*', { count: 'exact', head: true })
    .eq('admin_id', adminId);

  if (countError || !count) return [];

  const chunkSize = 1000;
  const all: Array<{
    id: string;
    name: string;
    quantity: number | null;
    purchase_price: number | null;
    cost_price: number | null;
    sell_price: number | null;
    unit_price: number | null;
    category: string | null;
    min_stock_level: number | null;
  }> = [];

  for (let offset = 0; offset < count; offset += chunkSize) {
    const { data, error } = await supabase
      .from('products')
      .select('id, name, quantity, purchase_price, cost_price, sell_price, unit_price, category, min_stock_level')
      .eq('admin_id', adminId)
      .order('name', { ascending: true })
      .range(offset, offset + chunkSize - 1);

    if (error) {
      console.error('Error fetching products chunk for analytics:', error);
      break;
    }
    if (data) all.push(...data);
  }

  return all;
}

export interface TopSellingItem {
  name: string;
  quantity: number;
  revenue: number;
}

export interface DailyCustomerCount {
  date: string;
  onlineCount: number;
  shopCount: number;
}

export interface OverviewAnalytics {
  inventory: {
    totalItems: number;
    totalQuantity: number;
    totalValue: number;
    lowStockCount: number;
    outOfStockCount: number;
    categories: number;
    avgItemValue: number;
    topCategories: Array<{ name: string; count: number; value: number }>;
    trends: {
      items: PeriodComparison;
      quantity: PeriodComparison;
      value: PeriodComparison;
    };
  };
  sales: {
    totalRevenue: number;
    onlineRevenue: number;
    shopRevenue: number;
    totalOrders: number;
    onlineOrders: number;
    shopOrders: number;
    avgOrderValue: number;
    paidOrders: number;
    unpaidOrders: number;
    cancelledOrders: number;
    topProducts: Array<{ name: string; quantity: number; revenue: number }>;
    topSellingToday: TopSellingItem[];
    topSellingWeek: TopSellingItem[];
    topSellingMonth: TopSellingItem[];
    topSellingAll: TopSellingItem[];
    soldNamesToday: string[];
    soldNamesWeek: string[];
    soldNamesMonth: string[];
    soldNamesAll: string[];
    allProducts: Array<{ name: string; quantity: number }>;
    revToday: number;
    revWeek: number;
    revMonth: number;
    qtyToday: number;
    qtyWeek: number;
    qtyMonth: number;
    dailyCustomers: DailyCustomerCount[];
    trends: {
      revenue: PeriodComparison;
      orders: PeriodComparison;
    };
    revenueTrend: TrendData[];
  };
  shipments: {
    incoming: number;
    outgoing: number;
    pending: number;
    delivered: number;
    trends: {
      incoming: PeriodComparison;
      outgoing: PeriodComparison;
    };
  };
  performance: {
    inventoryTurnover: number;
    fulfillmentRate: number;
  };
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
}

const emptyComparison: PeriodComparison = { current: 0, previous: 0, change: 0, changePercent: 0, isPositive: true };

function isWithinDays(dateStr: string, days: number): boolean {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  return d >= start && d <= now;
}

function isToday(dateStr: string): boolean {
  const now = new Date();
  const d = new Date(dateStr);
  return d.toISOString().split('T')[0] === now.toISOString().split('T')[0];
}

function isWithinCalendarWeek(dateStr: string): boolean {
  const { weekStart, weekEnd } = getWeekBounds();
  const d = new Date(dateStr).toISOString().split('T')[0];
  return d >= weekStart && d <= weekEnd;
}

export function useOverviewAnalytics(
  adminId: string | null,
  timeRange: TimeRange,
  comparisonEnabled: boolean = false
): OverviewAnalytics {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [analytics, setAnalytics] = useState<Omit<OverviewAnalytics, 'loading' | 'error' | 'lastUpdated' | 'refetch'>>({
    inventory: {
      totalItems: 0, totalQuantity: 0, totalValue: 0, lowStockCount: 0, outOfStockCount: 0,
      categories: 0, avgItemValue: 0, topCategories: [],
      trends: { items: emptyComparison, quantity: emptyComparison, value: emptyComparison },
    },
    sales: {
      totalRevenue: 0, onlineRevenue: 0, shopRevenue: 0,
      totalOrders: 0, onlineOrders: 0, shopOrders: 0, avgOrderValue: 0,
      paidOrders: 0, unpaidOrders: 0, cancelledOrders: 0, topProducts: [],
      topSellingToday: [], topSellingWeek: [], topSellingMonth: [], topSellingAll: [],
      soldNamesToday: [], soldNamesWeek: [], soldNamesMonth: [], soldNamesAll: [],
      allProducts: [],
      revToday: 0, revWeek: 0, revMonth: 0, qtyToday: 0, qtyWeek: 0, qtyMonth: 0,
      dailyCustomers: [],
      trends: { revenue: emptyComparison, orders: emptyComparison },
      revenueTrend: [],
    },
    shipments: {
      incoming: 0, outgoing: 0, pending: 0, delivered: 0,
      trends: { incoming: emptyComparison, outgoing: emptyComparison },
    },
    performance: { inventoryTurnover: 0, fulfillmentRate: 0 },
  });

  const fetchAnalytics = useCallback(async () => {
    if (!adminId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { start: currentStart, end: currentEnd } = getDateRangeForPeriod(timeRange);
      const { start: previousStart, end: previousEnd } = comparisonEnabled
        ? getPreviousPeriodRange(timeRange)
        : { start: currentStart, end: currentEnd };

      // --- Inventory (aggregates via RPC — same source as Inventory Management) ---
      const { data: statsData, error: statsError } = await supabase.rpc('get_inventory_stats_v2', {
        p_admin_id: adminId,
      });
      if (statsError) throw statsError;

      const statsRow = Array.isArray(statsData) ? statsData[0] : statsData;
      const totalItems = Number(statsRow?.total_items ?? 0);
      const totalQuantity = Number(statsRow?.total_quantity ?? 0);
      const totalValue = parseFloat(String(statsRow?.cost_value ?? 0)) || 0;
      const lowStockCount = Number(statsRow?.low_stock ?? 0);
      const outOfStockCount = Number(statsRow?.out_of_stock ?? 0);
      const categories = Number(statsRow?.categories ?? 0);
      const avgItemValue = totalItems > 0 ? totalValue / totalItems : 0;

      // Per-product data for category breakdown (paginated — avoids 1000-row cap)
      const currentItems = await fetchAllProductsForAdmin(adminId);

      const categoryMap = new Map<string, { count: number; value: number }>();
      currentItems.forEach((item) => {
        const cat = item.category || 'Uncategorized';
        const existing = categoryMap.get(cat) || { count: 0, value: 0 };
        const buyPrice = item.purchase_price || item.cost_price || 0;
        categoryMap.set(cat, {
          count: existing.count + 1,
          value: existing.value + (buyPrice * (item.quantity || 0)),
        });
      });
      const topCategories = Array.from(categoryMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      let previousTotalItems = 0;
      let previousTotalQuantity = 0;
      let previousTotalValue = 0;

      if (comparisonEnabled) {
        const { data: previousProducts } = await supabase
          .from('products')
          .select('id, quantity, purchase_price, cost_price')
          .eq('admin_id', adminId)
          .gte('created_at', previousStart.toISOString())
          .lte('created_at', previousEnd.toISOString());

        const prevItems = previousProducts || [];
        previousTotalItems = prevItems.length;
        previousTotalQuantity = prevItems.reduce((sum, p) => sum + (p.quantity || 0), 0);
        previousTotalValue = prevItems.reduce((sum, p) => {
          const buyPrice = p.purchase_price || p.cost_price || 0;
          return sum + (buyPrice * (p.quantity || 0));
        }, 0);
      }

      // --- Fetch Shopify analytics from API (direct from Shopify) ---
      const startStr = currentStart.toISOString().split('T')[0];
      const endStr = currentEnd.toISOString().split('T')[0];
      const analyticsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fetch-analytics`;
      const analyticsRes = await fetch(analyticsUrl, {
        method: 'POST',
        headers: await getValidAuthHeadersOrThrow(),
        body: JSON.stringify({ startDate: startStr, endDate: endStr }),
      });
      const analyticsData = analyticsRes.ok
        ? await analyticsRes.json().catch(() => ({}))
        : { onlineRevenue: 0, onlineOrders: 0, shopRevenue: 0, shopOrders: 0, totalRevenue: 0, totalOrders: 0, dailyBreakdown: [], topProducts: [], orders: [] };

      let previousRevenue = 0;
      let previousOrders = 0;
      if (comparisonEnabled) {
        const prevStartStr = previousStart.toISOString().split('T')[0];
        const prevEndStr = previousEnd.toISOString().split('T')[0];
        const prevRes = await fetch(analyticsUrl, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ startDate: prevStartStr, endDate: prevEndStr }),
        });
        const prevData = prevRes.ok ? await prevRes.json().catch(() => ({})) : {};
        previousRevenue = prevData.totalRevenue ?? 0;
        previousOrders = ((prevData.onlineOrders ?? 0) + (prevData.shopOrders ?? 0)) || 0;
      }

      const onlineRevenue = analyticsData.onlineRevenue ?? 0;
      const onlineOrders = analyticsData.onlineOrders ?? 0;
      const shopRevenue = analyticsData.shopRevenue ?? 0;
      const shopOrders = analyticsData.shopOrders ?? 0;
      const totalRevenue = analyticsData.totalRevenue ?? 0;
      const totalOrders = analyticsData.totalOrders ?? 0;
      const shopifyOrders = analyticsData.orders ?? [];

      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      const paidOrders = totalOrders;
      const cancelledOrders = 0;
      const unpaidOrders = 0;
      const fulfilledOrders = totalOrders;

      const topProducts = (analyticsData.topProducts ?? []).slice(0, 10).map((p: { name: string; quantity: number; revenue: number }) => ({
        name: p.name,
        quantity: p.quantity,
        revenue: p.revenue,
      }));

      const trendPoints: Array<{ date: string; value: number }> = (analyticsData.dailyBreakdown ?? []).map(
        (d: { date: string; onlineRevenue?: number; shopRevenue?: number }) => ({
          date: d.date,
          value: (d.onlineRevenue ?? 0) + (d.shopRevenue ?? 0),
        })
      );
      const revenueTrend = formatTrendData(trendPoints, timeRange.days > 90 ? 'week' : 'day');

      const aggregateFromOrders = (orders: any[], filterFn: (d: string) => boolean): TopSellingItem[] => {
        const map = new Map<string, { quantity: number; revenue: number }>();
        orders.filter((o: any) => filterFn(o.createdAt?.split?.('T')[0] ?? '')).forEach((order: any) => {
          for (const edge of order.lineItems?.edges ?? []) {
            const node = edge.node;
            const name = node?.title ?? 'Unknown';
            const qty = node?.quantity ?? 0;
            const price = parseFloat(node?.originalUnitPriceSet?.shopMoney?.amount ?? '0');
            const existing = map.get(name) || { quantity: 0, revenue: 0 };
            existing.quantity += qty;
            existing.revenue += price * qty;
            map.set(name, existing);
          }
        });
        return Array.from(map.entries())
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.quantity - a.quantity)
          .slice(0, 10);
      };

      const topSellingToday = aggregateFromOrders(shopifyOrders, isToday);
      const topSellingWeek = aggregateFromOrders(shopifyOrders, isWithinCalendarWeek);
      const topSellingMonth = aggregateFromOrders(shopifyOrders, d => isWithinDays(d, 30));
      const topSellingAll = aggregateFromOrders(shopifyOrders, () => true);

      const getSoldNamesForPeriod = (orders: any[], filterFn: (d: string) => boolean): Set<string> => {
        const names = new Set<string>();
        orders.filter((o: any) => filterFn(o.createdAt?.split?.('T')[0] ?? '')).forEach((order: any) => {
          for (const edge of order.lineItems?.edges ?? []) {
            const name = edge.node?.title;
            if (name) names.add(name);
          }
        });
        return names;
      };
      const soldNamesToday = getSoldNamesForPeriod(shopifyOrders, isToday);
      const soldNamesWeek = getSoldNamesForPeriod(shopifyOrders, isWithinCalendarWeek);
      const soldNamesMonth = getSoldNamesForPeriod(shopifyOrders, d => isWithinDays(d, 30));
      const soldNamesAll = getSoldNamesForPeriod(shopifyOrders, () => true);

      const allInStockProducts = currentItems
        .filter(p => p.name && (p.quantity || 0) > 0)
        .map(p => ({ name: p.name, quantity: p.quantity || 0 }));

      const dailyBreakdown = analyticsData.dailyBreakdown ?? [];
      const revToday = dailyBreakdown
        .filter((d: any) => isToday(d.date))
        .reduce((s: number, d: any) => s + (d.onlineRevenue ?? 0) + (d.shopRevenue ?? 0), 0);
      const revWeek = dailyBreakdown
        .filter((d: any) => isWithinCalendarWeek(d.date))
        .reduce((s: number, d: any) => s + (d.onlineRevenue ?? 0) + (d.shopRevenue ?? 0), 0);
      const revMonth = dailyBreakdown
        .filter((d: any) => isWithinDays(d.date, 30))
        .reduce((s: number, d: any) => s + (d.onlineRevenue ?? 0) + (d.shopRevenue ?? 0), 0);

      const qtyFromShopifyOrders = (orders: any[]) =>
        orders.reduce((sum: number, o: any) => {
          let qty = 0;
          for (const edge of o.lineItems?.edges ?? []) {
            qty += edge.node?.quantity ?? 0;
          }
          return sum + qty;
        }, 0);
      const ordersToday = shopifyOrders.filter((o: any) => isToday(o.createdAt?.split?.('T')[0] ?? ''));
      const ordersWeek = shopifyOrders.filter((o: any) => isWithinCalendarWeek(o.createdAt?.split?.('T')[0] ?? ''));
      const ordersMonth = shopifyOrders.filter((o: any) => isWithinDays(o.createdAt?.split?.('T')[0] ?? '', 30));
      const qtyToday = qtyFromShopifyOrders(ordersToday);
      const qtyWeek = qtyFromShopifyOrders(ordersWeek);
      const qtyMonth = qtyFromShopifyOrders(ordersMonth);

      const dailyCustomers: DailyCustomerCount[] = Array.from(
        new Set(dailyBreakdown.map((d: any) => d.date))
      )
        .sort()
        .map((date: string) => {
          const d = dailyBreakdown.find((x: any) => x.date === date);
          return {
            date,
            onlineCount: d?.onlineCount ?? 0,
            shopCount: d?.shopCount ?? 0,
          };
        });

      const fulfilled = (o: { displayFulfillmentStatus?: string | null }) => {
        const f = (o.displayFulfillmentStatus ?? '').toUpperCase();
        return f === 'FULFILLED' || f === 'PARTIALLY_FULFILLED';
      };
      const unfulfilled = (o: { displayFulfillmentStatus?: string | null }) => {
        const f = (o.displayFulfillmentStatus ?? '').toUpperCase();
        return !f || f === 'UNFULFILLED';
      };
      const outgoing = shopifyOrders.filter((o: any) => fulfilled(o)).length;
      const pending = shopifyOrders.filter((o: any) => unfulfilled(o)).length;
      const delivered = shopifyOrders.filter((o: any) => fulfilled(o) && !!String(o.tracking_code ?? '').trim()).length;
      const previousOutgoing = 0;

      const { data: currentIncomingShipments } = await supabase
        .from('shipments')
        .select('id')
        .eq('admin_id', adminId)
        .eq('shipment_type', 'incoming')
        .gte('created_at', currentStart.toISOString())
        .lte('created_at', currentEnd.toISOString());
      const { data: previousIncomingShipments } = comparisonEnabled
        ? await supabase
            .from('shipments')
            .select('id')
            .eq('admin_id', adminId)
            .eq('shipment_type', 'incoming')
            .gte('created_at', previousStart.toISOString())
            .lte('created_at', previousEnd.toISOString())
        : { data: null };
      const incoming = (currentIncomingShipments || []).length;
      const previousIncoming = (previousIncomingShipments || []).length;

      const inventoryTurnover = totalValue > 0 ? totalRevenue / totalValue : 0;
      const fulfillmentRate = totalOrders > 0 ? (fulfilledOrders / totalOrders) * 100 : 0;

      setAnalytics({
        inventory: {
          totalItems, totalQuantity, totalValue, lowStockCount, outOfStockCount,
          categories, avgItemValue, topCategories,
          trends: {
            items: calculatePeriodComparison(totalItems, previousTotalItems),
            quantity: calculatePeriodComparison(totalQuantity, previousTotalQuantity),
            value: calculatePeriodComparison(totalValue, previousTotalValue),
          },
        },
        sales: {
          totalRevenue, onlineRevenue, shopRevenue,
          totalOrders, onlineOrders, shopOrders,
          avgOrderValue,
          paidOrders, unpaidOrders, cancelledOrders, topProducts,
          topSellingToday, topSellingWeek, topSellingMonth, topSellingAll,
          soldNamesToday: Array.from(soldNamesToday),
          soldNamesWeek: Array.from(soldNamesWeek),
          soldNamesMonth: Array.from(soldNamesMonth),
          soldNamesAll: Array.from(soldNamesAll),
          allProducts: allInStockProducts,
          revToday, revWeek, revMonth, qtyToday, qtyWeek, qtyMonth,
          dailyCustomers,
          trends: {
            revenue: calculatePeriodComparison(totalRevenue, previousRevenue),
            orders: calculatePeriodComparison(totalOrders, previousOrders),
          },
          revenueTrend,
        },
        shipments: {
          incoming, outgoing, pending, delivered,
          trends: {
            incoming: calculatePeriodComparison(incoming, previousIncoming),
            outgoing: calculatePeriodComparison(outgoing, previousOutgoing),
          },
        },
        performance: { inventoryTurnover, fulfillmentRate },
      });

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching overview analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [adminId, timeRange, comparisonEnabled]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    ...analytics,
    loading,
    error,
    lastUpdated,
    refetch: fetchAnalytics,
  };
}
